// Section 22 History and 24.14: <SeriesChart series markers>, one metric over the run as an
// inline SVG: a 2px accent line with a 10% wash, hairline grid, year ticks, the last value
// labelled at the end, and event (diamond) and death (cross) markers on a rail above the plot.
// Hover or focus plus arrow keys move a crosshair that reads out the season, value and markers.
import { useState } from 'preact/hooks';
import { domainOf, linear, niceTicks, pathOf } from './scale';
import './charts.css';

export interface Marker {
  /** Index into the series. */
  index: number;
  kind: 'event' | 'death';
  /** What the crosshair readout says for this marker. */
  text: string;
}

interface Props {
  title: string;
  values: readonly number[];
  markers: readonly Marker[];
  /** Readout for one point: "Winter, year 3: 21". */
  describe: (index: number) => string;
  /** Tick label for a point, or null for no tick (e.g. only the first season of a year). */
  tick: (index: number) => string | null;
  /** Format a value for the axis and end label. */
  format: (v: number) => string;
  /** Accessible summary of the whole series. */
  summary: string;
}

// Drawn at about the size it is shown (two columns of ~420 px), so 11 px text stays 11 px.
const W = 420;
const H = 140;
const M = { top: 16, right: 40, bottom: 20, left: 38 };

export function SeriesChart({ title, values, markers, describe, tick, format, summary }: Props) {
  const [active, setActive] = useState<number | null>(null);
  const n = values.length;
  const [lo, hi] = domainOf(values);
  // A single season sits in the middle of the plot, not on the y-axis labels (M7).
  const x =
    n === 1 ? () => (M.left + W - M.right) / 2 : linear(0, Math.max(1, n - 1), M.left, W - M.right);
  const y = linear(lo, hi, H - M.bottom, M.top);
  const points = values.map((v, i): [number, number] => [x(i), y(v)]);
  const last = points[n - 1];
  const lastValue = values[n - 1];
  const area =
    n > 1 ? `${pathOf(points)}L${x(n - 1).toFixed(1)},${y(lo)}L${x(0).toFixed(1)},${y(lo)}Z` : '';
  const move = (index: number): void => setActive(Math.max(0, Math.min(n - 1, index)));
  const readout =
    active === null
      ? null
      : [describe(active), ...markers.filter((m) => m.index === active).map((m) => m.text)].join(
          ' · ',
        );
  return (
    <figure class="chart">
      <figcaption class="chart__title">{title}</figcaption>
      <svg
        class="chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={summary}
        tabIndex={0}
        onPointerMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          if (rect.width === 0 || n === 0) return;
          const px = ((e.clientX - rect.left) / rect.width) * W;
          move(Math.round(((px - M.left) / (W - M.left - M.right)) * (n - 1)));
        }}
        onPointerLeave={() => setActive(null)}
        onBlur={() => setActive(null)}
        onFocus={() => setActive(n - 1)}
        onKeyDown={(e) => {
          const at = active ?? n - 1;
          if (e.key === 'ArrowLeft') move(at - 1);
          else if (e.key === 'ArrowRight') move(at + 1);
          else if (e.key === 'Home') move(0);
          else if (e.key === 'End') move(n - 1);
          else return;
          e.preventDefault();
        }}
      >
        {niceTicks(lo, hi).map((t) => (
          <g key={t}>
            <line class="chart__grid" x1={M.left} x2={W - M.right} y1={y(t)} y2={y(t)} />
            <text class="chart__axis" x={M.left - 6} y={y(t) + 4} text-anchor="end">
              {format(t)}
            </text>
          </g>
        ))}
        {values.map((_, i) => {
          const label = tick(i);
          return label === null ? null : (
            <text key={i} class="chart__axis" x={x(i)} y={H - 4} text-anchor="middle">
              {label}
            </text>
          );
        })}
        {n > 1 && <path class="chart__area" d={area} />}
        {n > 1 && <path class="chart__line" d={pathOf(points)} />}
        {markers.map((m, i) =>
          m.kind === 'event' ? (
            <path
              key={`e${i}`}
              class="chart__marker chart__marker--event"
              d={`M${x(m.index)},${M.top - 12}l4,4l-4,4l-4,-4Z`}
            />
          ) : (
            <path
              key={`d${i}`}
              class="chart__marker chart__marker--death"
              d={`M${x(m.index) - 3.5},${M.top - 11}l7,7M${x(m.index) + 3.5},${M.top - 11}l-7,7`}
            />
          ),
        )}
        {last !== undefined && lastValue !== undefined && (
          <g>
            <circle class="chart__dot" cx={last[0]} cy={last[1]} r={4} />
            <text class="chart__end" x={last[0] + 7} y={last[1] + 4}>
              {format(lastValue)}
            </text>
          </g>
        )}
        {active !== null && points[active] !== undefined && (
          <g>
            <line class="chart__cross" x1={x(active)} x2={x(active)} y1={M.top} y2={H - M.bottom} />
            <circle class="chart__dot" cx={x(active)} cy={points[active][1]} r={4} />
          </g>
        )}
      </svg>
      <p class="chart__readout" aria-live="polite">
        {readout ?? ' '}
      </p>
    </figure>
  );
}
